/**
 * The Construction Kit (docs/spec/07 §4): a 640 x 360 scene that edits one cave. The document
 * lives in `KitState`; this class routes the pointer and keys, and draws the panels.
 */

import { buildCave } from '../../caves/build';
import { charOf } from '../../caves/legend';
import type { CaveDef } from '../../caves/types';
import type { Atlas } from '../../render/atlas';
import { atlasFor } from '../../render/atlas';
import type { Screen } from '../../render/screen';
import type { KeyInput, MenuAction, PointerInput, Scene, SceneHost } from '../../ui/scene';
import type { KitDoc } from '../model/doc';
import { docFromDef, newDoc } from '../model/doc';
import type { Point } from '../model/grid';
import { cellAt } from '../model/grid';
import { elementName } from '../model/palette';
import type { ToolId } from '../model/shapes';
import type { TextField } from '../model/text';
import type { View } from '../model/view';
import { cellAtPoint, clampView, revealCell, viewOrigin } from '../model/view';
import type { Command, KitApi, Sound } from './api';
import { pasteCode, runCommand } from './commands';
import { drawForm } from './formPanel';
import { drawHeader, drawStatus } from './header';
import { KitInput } from './input';
import { SCREEN_H, SCREEN_W, VIEW } from './layout';
import type { Modal } from './modals/modal';
import { Painter } from './painter';
import { drawSide } from './sidePanel';
import { KitState } from './state';
import { drawView } from './viewPanel';
import { Ui } from './widgets';
import { loadWorkingCopy } from './workingCopy';

/** 1x when the whole cave fits (an intermission), else 1/2x, so a standard cave opens in one view. */
function fittingView(doc: KitDoc): View {
  const cell = doc.grid.w * 16 <= VIEW.w && doc.grid.h * 16 <= VIEW.h ? 16 : 8;
  return { cell, sx: 0, sy: 0 };
}

function startCell(doc: KitDoc): Point {
  const i = doc.grid.cells.indexOf('P');
  return i >= 0 ? { x: i % doc.grid.w, y: Math.floor(i / doc.grid.w) } : { x: 1, y: 1 };
}

export class KitScene implements Scene, KitApi {
  readonly st: KitState;
  readonly ui = new Ui();
  readonly painter: Painter;
  tool: ToolId = 'pencil';
  element = 'w';
  view: View;
  showGrid = false;
  preview = false;
  cursor: Point;
  focus: 'cave' | 'form' = 'cave';
  formIndex = 0;
  editing: TextField | null = null;
  /** The pointer is over the view, or the keyboard moved the cursor last. */
  cursorShown = false;
  private readonly input: KitInput;
  private modals: Modal[] = [];
  private clock = 0;
  private prevTool: ToolId = 'pencil';
  private rolled: { doc: KitDoc; cells: string[] } | null = null;
  private readonly onPaste = (e: ClipboardEvent): void => {
    const text = e.clipboardData?.getData('text') ?? '';
    const top = this.modals[this.modals.length - 1];
    if (top?.paste) top.paste(text);
    else if (!top) pasteCode(this, text);
  };

  constructor(readonly host: SceneHost, def?: CaveDef) {
    const services = host.services;
    const copy = def ? null : loadWorkingCopy(services.storage);
    const doc = def ? docFromDef(def) : copy ? docFromDef(copy.def) : newDoc();
    // A cave handed in (a shared link, a set cave) is not in the library: it counts as unsaved.
    const dirty = def ? true : (copy?.dirty ?? false);
    this.st = new KitState(services.storage, (t) => services.announce(t), doc, copy?.key, dirty);
    this.painter = new Painter(this.st, this);
    this.view = clampView(fittingView(doc), doc.grid, VIEW);
    this.cursor = startCell(doc);
    this.input = new KitInput(this);
    // For the console and browser tests, as `window.__game` is for the game.
    (window as unknown as { __kit?: KitScene }).__kit = this;
  }

  size(): { w: number; h: number } {
    return { w: SCREEN_W, h: SCREEN_H };
  }

  enter(): void {
    this.host.services.audio.music(null);
    window.addEventListener('paste', this.onPaste);
  }

  leave(): void {
    window.removeEventListener('paste', this.onPaste);
    this.painter.cancel();
    this.input.release();
    this.st.persist();
  }

  // ---- KitApi ----------------------------------------------------------------------------------

  atlas(): Atlas {
    return atlasFor(this.st.doc.colors);
  }

  picked(ch: string): void {
    this.element = ch;
    this.tool = this.prevTool;
    this.sound('select');
    this.st.say(`PICKED ${elementName(ch)}`);
  }

  selectElement(ch: string): void {
    if (ch !== this.element) this.sound('move');
    this.element = ch;
  }

  selectTool(id: ToolId): void {
    if (id === 'picker' && this.tool !== 'picker') this.prevTool = this.tool;
    this.painter.cancel();
    if (id !== this.tool) this.sound('move');
    this.tool = id;
  }

  run(c: Command): void {
    runCommand(this, c);
  }

  big(): boolean {
    return this.input.big();
  }

  sound(s: Sound): void {
    this.host.services.audio.ui(s);
  }

  openModal(m: Modal): void {
    this.painter.cancel();
    this.editing = null;
    this.modals.push(m);
  }

  closeModal(): void {
    this.modals.pop();
  }

  moveCursor(p: Point): void {
    const g = this.st.doc.grid;
    this.cursor = { x: Math.min(g.w - 1, Math.max(0, p.x)), y: Math.min(g.h - 1, Math.max(0, p.y)) };
    this.view = revealCell(this.view, g, VIEW, this.cursor);
    this.cursorShown = true;
    this.painter.move(this.cursor);
  }

  loadDoc(doc: KitDoc, key: string | undefined, clean: boolean): void {
    this.painter.cancel();
    this.st.load(doc, key, clean);
    this.view = clampView(fittingView(doc), doc.grid, VIEW);
    this.cursor = startCell(doc);
    this.editing = null;
  }

  // ---- Scene -----------------------------------------------------------------------------------

  update(dtMs: number): void {
    this.clock += dtMs;
    this.st.update(dtMs);
    this.input.update(dtMs);
    this.modals[this.modals.length - 1]?.update?.(dtMs);
    // The grid may have shrunk under the cursor or the view (resize, undo, load).
    const g = this.st.doc.grid;
    const c = this.cursor;
    if (c.x >= g.w || c.y >= g.h) this.cursor = { x: Math.min(c.x, g.w - 1), y: Math.min(c.y, g.h - 1) };
    this.view = clampView(this.view, g, VIEW);
  }

  /** What each cell shows: the map, or with the fill preview on, `?` rolled as at level 1. */
  private shownCells(): readonly string[] {
    const doc = this.st.doc;
    if (!this.preview) return doc.grid.cells;
    if (this.rolled?.doc !== doc) {
      const built = buildCave(this.st.info.def, 1);
      this.rolled = { doc, cells: doc.grid.cells.map((ch, i) => (ch === '?' ? charOf(built.cells[i] ?? 0) : ch)) };
    }
    return this.rolled.cells;
  }

  draw(screen: Screen): void {
    this.ui.begin(screen, Math.floor(this.clock / 40));
    screen.clear(0);
    const cells = this.shownCells();
    const hoverCell = cellAtPoint(this.view, this.st.doc.grid, VIEW, this.ui.px - VIEW.x, this.ui.py - VIEW.y);
    drawHeader(this);
    drawView(this, { cells, cursor: this.cursorShown && this.modals.length === 0 });
    drawSide(this);
    drawForm(this);
    const cell = hoverCell ?? (this.cursorShown ? this.cursor : null);
    drawStatus(this, cell, cell ? cells[cell.y * this.st.doc.grid.w + cell.x] : undefined);
    for (const m of this.modals) m.draw(this.ui);
  }

  pointer(p: PointerInput): void {
    this.ui.px = p.x;
    this.ui.py = p.y;
    if (p.kind === 'wheel') return void this.ui.at(p.x, p.y)?.wheel?.(p.delta);
    const g = this.st.doc.grid;
    const over = cellAtPoint(this.view, g, VIEW, p.x - VIEW.x, p.y - VIEW.y);
    if (p.kind === 'move') {
      if (this.input.pan(p.x, p.y)) return;
      this.input.moved(p.x, p.y);
      if (over && this.modals.length === 0) {
        this.cursor = over;
        this.cursorShown = true;
      } else if (!this.painter.stroke) this.cursorShown = false;
      // A drag keeps going past the edge of the view, clamped to the cave.
      if (this.painter.stroke && !this.painter.stroke.keyboard) this.painter.move(this.clampedCell(p.x, p.y));
      return;
    }
    if (p.kind === 'up') {
      this.input.up();
      if (!this.painter.stroke?.keyboard) this.painter.end();
      return;
    }
    // A press. A lost release (outside the window) must not leave a stroke hanging.
    this.input.up();
    if (!this.painter.stroke?.keyboard) this.painter.end();
    this.st.history.seal();
    const zone = this.ui.at(p.x, p.y);
    if (this.modals.length === 0 && p.button === 1 && zone?.id === 'view') return this.input.startPan(p.x, p.y);
    if (zone?.id === 'view' && this.modals.length === 0) {
      this.editing = null;
      this.focus = 'cave';
      if (over) this.painter.begin(over, p.button);
      return;
    }
    if (this.editing && zone?.id !== this.editing) this.editing = null;
    if (zone?.click) {
      zone.click(p.button);
      if (zone.repeat) this.input.hold(zone);
    }
  }

  /** The cell under a screen point, clamped into the cave (for drags that leave the view). */
  private clampedCell(x: number, y: number): Point {
    const g = this.st.doc.grid;
    const o = viewOrigin(this.view, g, VIEW);
    const c = this.view.cell;
    const cx = Math.floor((x - VIEW.x - o.x) / c);
    const cy = Math.floor((y - VIEW.y - o.y) / c);
    return { x: Math.min(g.w - 1, Math.max(0, cx)), y: Math.min(g.h - 1, Math.max(0, cy)) };
  }

  keydown(k: KeyInput): boolean {
    return this.input.key(k, this.modals[this.modals.length - 1] ?? null);
  }

  action(a: MenuAction): void {
    this.input.action(a, this.modals[this.modals.length - 1] ?? null);
  }

  /** For tests and the debug console: the numbers that matter. */
  debugState(): Record<string, unknown> {
    const info = this.st.info;
    return {
      name: this.st.doc.name, w: this.st.doc.grid.w, h: this.st.doc.grid.h, tool: this.tool, element: this.element,
      errors: info.errors, issues: info.issues.map((i) => i.message), dirty: this.st.dirty, modals: this.modals.length,
      undo: this.st.history.undoCount, cursor: this.cursor,
      cell: cellAt(this.st.doc.grid, this.cursor.x, this.cursor.y),
    };
  }
}
